import { useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, CalendarRange, MapPin, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loading, Empty, SiteStatusBadge, InfoRow, ConfirmDialog, Field, Kpi } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SiteForm } from "../components/Forms";
import DocumentsPanel from "../components/DocumentsPanel";
import FinancePanel from "../components/finance/FinancePanel";
import { EditTimeButton, useAssignmentEdit } from "../components/AssignmentEdit";
import Timeline, { Legend } from "../components/Timeline";
import { eur, fmtDate, num, todayISO, weekStart, addDays } from "../lib/format";
import { BudgetMeter } from "../components/BudgetAlert";

function AssignList({ items, canEdit, onDelete, onEdit, type }) {
  const list = items.filter((a) => a.resource_type === type);
  if (!list.length) return <Empty title={type === "worker" ? "Nessun operaio assegnato" : "Nessun mezzo assegnato"} />;
  const today = todayISO();
  return (
    <div className="divide-y divide-border/60">
      {list.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`site-assignment-${a.id}`}>
          <div className="flex-1 min-w-[180px]">
            <Link to={type === "worker" ? `/operai/${a.worker_id}` : `/mezzi/${a.vehicle_id}`} className="font-semibold hover:text-amber-300">{a.resource_name}</Link>
            <div className="text-xs text-muted-foreground">{a.qualification} · {a.shift}</div>
          </div>
          <div className="text-sm num">{fmtDate(a.start_date)} → {fmtDate(a.end_date)}</div>
          <div className="text-xs text-muted-foreground w-20 text-right">{a.days} gg{type === "worker" && ` · ${num(a.days * a.hours_per_day)} h`}</div>
          <div className="num text-amber-300 w-24 text-right">{eur(a.cost)}</div>
          {a.end_date >= today && <span className="text-[10px] uppercase text-emerald-400 w-16">{a.start_date <= today ? "in corso" : "futuro"}</span>}
          {canEdit && <EditTimeButton onClick={() => onEdit(a)} testid={`edit-assignment-time-${a.id}`} />}
          {canEdit && <Button size="icon" variant="ghost" className="text-red-400" onClick={() => onDelete(a)} data-testid={`delete-assignment-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
    </div>
  );
}

function Diary({ site, reload }) {
  const [f, setF] = useState({ entry_date: todayISO(), weather: "Sereno", text: "" });
  const add = async () => {
    if (!f.text.trim()) return toast.error("Inserisci il testo");
    try {
      await api.post(`/sites/${site.id}/diary`, f);
      toast.success("✓ Voce aggiunta al diario");
      setF({ ...f, text: "" });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <div className="grid sm:grid-cols-[160px_160px_1fr_auto] gap-2 items-end mb-5 p-3 rounded-lg border border-dashed border-border">
        <Field label="Data"><Input type="date" value={f.entry_date} onChange={(e) => setF({ ...f, entry_date: e.target.value })} /></Field>
        <Field label="Meteo"><Input value={f.weather} onChange={(e) => setF({ ...f, weather: e.target.value })} /></Field>
        <Field label="Annotazione"><Textarea rows={1} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="diary-text-input" /></Field>
        <Button onClick={add} data-testid="diary-add-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
      </div>
      {!site.diary.length ? <Empty title="Diario vuoto" /> : (
        <ol className="relative border-l border-border ml-2">
          {site.diary.map((d) => (
            <li key={d.id} className="ml-5 pb-5">
              <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-amber-500" />
              <div className="text-xs text-muted-foreground num">{fmtDate(d.entry_date)} · {d.weather} · {d.author}</div>
              <div className="text-sm mt-1">{d.text}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const BAR_TONE = { attenzione: "bg-orange-500", superato: "bg-red-500" };

function CostsTab({ site }) {
  const c = site.costs;
  const u = site.budget_usage;
  const spent = u ? u.costs : c.total.total_cost;
  const value = u ? u.value : site.budget;
  const pct = value ? Math.min((spent / value) * 100, 100) : 0;
  const rows = [["day", "Giornaliero"], ["week", "Settimanale"], ["month", "Mensile"], ["total", "Totale pianificato"]];
  return (
    <div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        {rows.map(([k, l]) => (
          <div key={k} className="surface p-4" data-testid={`site-cost-${k}`}>
            <div className="eyebrow">{l}</div>
            {c[k].start && <div className="text-[11px] text-muted-foreground num">{fmtDate(c[k].start)}{c[k].start !== c[k].end && ` → ${fmtDate(c[k].end)}`}</div>}
            <div className="num text-2xl font-semibold mt-2 text-amber-300">{eur(c[k].total_cost)}</div>
            <div className="text-xs text-muted-foreground mt-2 space-y-0.5 num">
              <div className="flex justify-between"><span>Manodopera ({num(c[k].hours)} h)</span><span>{eur(c[k].labor_cost)}</span></div>
              <div className="flex justify-between"><span>Mezzi</span><span>{eur(c[k].vehicle_cost)}</span></div>
            </div>
          </div>
        ))}
      </div>
      <div className="surface p-4">
        <div className="flex justify-between text-sm mb-2"><span>{u ? "Tutti i costi su Valore cantiere" : "Avanzamento costi su budget"}</span><span className="num" data-testid="site-costs-progress-text">{eur(spent)} / {eur(value)}</span></div>
        <div className="h-3 rounded-full bg-background overflow-hidden"><div className={`h-full ${BAR_TONE[u?.level] || "bg-amber-500"}`} style={{ width: `${pct}%` }} /></div>
        <div className="text-xs text-muted-foreground mt-2">Costo operaio = ore × costo aziendale orario · Costo mezzo = giorni × costo giornaliero</div>
      </div>
    </div>
  );
}

function SiteHeader({ s, isAdmin, onPlan, onEdit, onDelete }) {
  return (
    <div className="surface p-5 sm:p-6 mb-6 fade-up relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-1 hazard-stripe opacity-70" />
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3"><span className="font-mono text-amber-400 text-sm">{s.code}</span><SiteStatusBadge status={s.status} /></div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1" data-testid="site-name">{s.name}</h1>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="h-4 w-4" />{s.address} · {s.client}</div>
        </div>
        {s.budget_usage?.pct != null && <BudgetMeter usage={s.budget_usage} className="lg:w-64" testid="site-header-budget-meter" />}
        <div className="flex flex-wrap gap-2">
          {s.status !== "chiuso" && <Button onClick={onPlan} data-testid="site-plan-btn"><CalendarRange className="h-4 w-4 mr-2" />Pianifica risorse</Button>}
          {isAdmin && <Button variant="outline" onClick={onEdit} data-testid="site-edit-btn"><Pencil className="h-4 w-4 mr-2" />Modifica</Button>}
          {isAdmin && <Button variant="outline" className="text-red-400 border-red-900" onClick={onDelete} data-testid="site-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
    </div>
  );
}

function SiteMap({ s }) {
  if (!s.latitude) return <Empty title="Coordinate non impostate" />;
  return (
    <iframe title="mappa" className="w-full h-full min-h-[240px] grayscale-[40%] invert-[88%] hue-rotate-180"
      src={`https://www.openstreetmap.org/export/embed.html?bbox=${s.longitude - 0.01},${s.latitude - 0.006},${s.longitude + 0.01},${s.latitude + 0.006}&layer=mapnik&marker=${s.latitude},${s.longitude}`} />
  );
}

function SiteInfoTab({ s, workersCount, vehiclesCount }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="surface p-5">
        <InfoRow label="Cliente / Committente" value={s.client} /><InfoRow label="Tipologia" value={s.site_type} /><InfoRow label="Indirizzo" value={s.address} />
        <InfoRow label="Coordinate" value={s.latitude ? `${s.latitude}, ${s.longitude}` : null} />
        <InfoRow label="Data inizio" value={fmtDate(s.start_date)} /><InfoRow label="Fine prevista" value={fmtDate(s.end_planned)} />
        <InfoRow label="Fine effettiva" value={fmtDate(s.end_actual)} /><InfoRow label={s.budget_from_quote ? "Budget (dal preventivo)" : "Budget"} value={eur(s.budget)} />
      </div>
      <div className="surface p-5">
        <div className="eyebrow mb-2">Responsabile</div>
        <div className="text-lg font-semibold" data-testid="site-manager">{s.manager_name || "—"}</div>
        {s.manager_phone && <a href={`tel:${s.manager_phone}`} className="text-sm text-amber-400">{s.manager_phone}</a>}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <Kpi label="Operai" value={workersCount} /><Kpi label="Mezzi" value={vehiclesCount} />
        </div>
        {s.notes && <div className="text-sm text-muted-foreground mt-4">{s.notes}</div>}
      </div>
      <div className="surface overflow-hidden min-h-[240px]"><SiteMap s={s} /></div>
    </div>
  );
}

function SiteCalendarTab({ assignments }) {
  const [calStart, setCalStart] = useState(weekStart(todayISO()));
  return (
    <div className="surface p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={() => setCalStart(addDays(calStart, -14))}><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setCalStart(weekStart(todayISO()))}>Oggi</Button>
          <Button size="icon" variant="outline" onClick={() => setCalStart(addDays(calStart, 14))}><ChevronRight className="h-4 w-4" /></Button>
          <span className="text-sm ml-2 num">{fmtDate(calStart)} → {fmtDate(addDays(calStart, 13))}</span>
        </div>
        <Legend />
      </div>
      <Timeline events={assignments.map((a) => ({ ...a, kind: "assignment" }))} start={calStart} days={14} />
    </div>
  );
}

const distinct = (items, key) => new Set(items.filter((a) => a[key]).map((a) => a[key])).size;

async function apiDelete(url, okMsg, after) {
  try {
    await api.delete(url);
    toast.success(okMsg);
    after();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

export default function SiteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [params] = useSearchParams();
  const { data: s, loading, reload } = useApi(`/sites/${id}`);
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const [delA, setDelA] = useState(null);
  const timeEdit = useAssignmentEdit(reload);

  if (loading && !s) return <Loading />;
  if (!s) return <Empty title="Cantiere non trovato o non accessibile" />;
  const removeSite = () => apiDelete(`/sites/${s.id}`, "✓ Cantiere eliminato", () => nav("/cantieri"));
  const removeAssignment = () => apiDelete(`/assignments/${delA.id}`, "✓ Assegnazione cancellata", reload).then(() => setDelA(null));
  const workersCount = distinct(s.assignments, "worker_id");
  const vehiclesCount = distinct(s.assignments, "vehicle_id");
  return (
    <div>
      <button onClick={() => nav("/cantieri")} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1 mb-4" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Cantieri</button>
      <SiteHeader s={s} isAdmin={isAdmin} onPlan={() => nav(`/pianificazione?site=${s.id}`)} onEdit={() => setEdit(true)} onDelete={() => setDel(true)} />
      <Tabs defaultValue={(isAdmin && params.get("tab")) || "dati"}>
        <TabsList className="mb-4 flex-wrap h-auto">
          {[["dati", "Dati generali"], ["operai", `Operai (${workersCount})`], ["mezzi", `Mezzi (${vehiclesCount})`], ["calendario", "Calendario"], ["costi", "Costi"], ["documenti", "Documenti"], ["diario", "Diario"], ...(isAdmin ? [["economia", "Economia"]] : [])].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`site-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dati"><SiteInfoTab s={s} workersCount={workersCount} vehiclesCount={vehiclesCount} /></TabsContent>
        <TabsContent value="operai"><div className="surface p-5"><AssignList items={s.assignments} type="worker" canEdit onDelete={setDelA} onEdit={timeEdit.ask} /></div></TabsContent>
        <TabsContent value="mezzi"><div className="surface p-5"><AssignList items={s.assignments} type="vehicle" canEdit onDelete={setDelA} onEdit={timeEdit.ask} /></div></TabsContent>
        <TabsContent value="calendario"><SiteCalendarTab assignments={s.assignments} /></TabsContent>
        <TabsContent value="costi"><CostsTab site={s} /></TabsContent>
        <TabsContent value="documenti"><div className="surface p-5"><DocumentsPanel entityType="site" entityId={s.id} canEdit /></div></TabsContent>
        <TabsContent value="diario"><div className="surface p-5"><Diary site={s} reload={reload} /></div></TabsContent>
        {isAdmin && <TabsContent value="economia"><FinancePanel siteId={s.id} /></TabsContent>}
      </Tabs>
      {timeEdit.dialog}
      <SiteForm open={edit} onOpenChange={setEdit} site={s} onSaved={reload} />
      <ConfirmDialog open={del} onOpenChange={setDel} title={`Eliminare il cantiere ${s.name}?`} description="Verranno eliminate anche tutte le assegnazioni e il diario." onConfirm={removeSite} />
      <ConfirmDialog open={!!delA} onOpenChange={() => setDelA(null)} title="Cancellare l'assegnazione?" description={delA && `${delA.resource_name} · ${fmtDate(delA.start_date)} → ${fmtDate(delA.end_date)}`} onConfirm={removeAssignment} confirmLabel="Cancella" />
    </div>
  );
}
